import { useState } from 'react'

import {
  GetUploadDocumentTaskFilesDocument,
  UploadDocumentTaskFileFragment,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import { type FileOptions } from 'modules/tasks/components/TaskDetails/Attachments/File'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { useBulkUpdateUploadDocumentTaskFiles } from 'modules/tasks/hooks/useBulkUpdateUploadDocumentTaskFiles'
import { useResolveTask } from 'modules/tasks/hooks/useResolveTask'

interface Props {
  title?: string
  description?: string
  task: TaskDetail
  employeeId: number
  documentName?: string
  folderId?: number
  canEmployeeManageAnyEmployeeFolder: boolean
}

export const useUploadDocumentFiles = ({
  documentName,
  task,
  employeeId,
  folderId,
  canEmployeeManageAnyEmployeeFolder,
}: Props) => {
  const employee = employees.get(Number(employeeId))

  const canEditEmployeeDocuments =
    employee?.policy.can('editDocuments') || canEmployeeManageAnyEmployeeFolder

  const { data, loading } = useQuery(GetUploadDocumentTaskFilesDocument, {
    variables: {
      taskId: task.id,
      folderId,
    },
    skip: !canEditEmployeeDocuments,
  })

  const files = data?.tasks.uploadDocumentTaskFiles || []

  const [filesBeingUploaded, setFilesBeingUploaded] = useState<File[]>([])
  const [filesBeingRemoved, setFilesBeingRemoved] = useState<number[]>([])

  const { resolveTask } = useResolveTask()

  const updateFiles = useBulkUpdateUploadDocumentTaskFiles()
  const shouldAutoResolve = task.type === 'upload_document' && !task.completedAt

  const currentFileOptionsWitName = documentName
    ? files.filter((file) => file.filename.includes(documentName))
    : files

  const currentFileOptions: FileOptions[] = currentFileOptionsWitName
    .filter((file) => !filesBeingRemoved.includes(file.id))
    .map((file) => {
      const fileName = file.filename
      const path = buildUrl(`/${file.path}`)

      return {
        fileName,
        file,
        newFile: false,
        inlinePath: path,
        options: [
          {
            href: path,
            title: i18n.t('common.download_file_name', { name: fileName }),
          },
          {
            onClick: () => onDeleteFile(file),
            title: i18n.t('common.delete'),
            type: 'wrong',
          },
        ],
      }
    })

  const { performAction: onAddFiles } = useAsyncAction(async (newFiles: File[]) => {
    setFilesBeingUploaded((prev) => [...prev, ...newFiles])

    const givenName = (file: File) =>
      documentName ? `${documentName}.${file.name.split('.').pop()}` : file.name

    const renamedFiles = newFiles.map(
      (file) => new File([file], givenName(file), { type: file.type })
    )

    await updateFiles({
      taskId: task.id,
      filesToAdd: renamedFiles,
      folderId,
    })

    setFilesBeingUploaded((prev) => prev.filter((f) => !newFiles.includes(f)))

    if (shouldAutoResolve)
      await resolveTask({
        variables: {
          id: task.id,
          done: true,
          identifier: task.identifier,
        },
        refecthVars: { taskId: task.id, taskIdentifier: task.identifier },
      })
  })

  const { performAction: onDeleteFile } = useAsyncAction(
    async (removedFile: UploadDocumentTaskFileFragment) => {
      setFilesBeingRemoved((prev) => [...prev, removedFile.id])

      await updateFiles({
        taskId: task.id,
        filesToRemove: [removedFile.id],
        folderId: folderId,
      })

      setFilesBeingRemoved((prev) => prev.filter((id) => id !== removedFile.id))
    }
  )

  const uploadingFileOptions: FileOptions[] = filesBeingUploaded.map((file) => {
    const fileName = file.name
    const givenName = documentName ? `${documentName}.${fileName.split('.').pop()}` : fileName

    return {
      newFile: true,
      fileName: givenName,
      options: [],
    }
  })

  const fileOptions = [...uploadingFileOptions, ...currentFileOptions]

  return {
    employee,
    loading,
    fileOptions,
    canEditEmployeeDocuments,
    fetchedFiles: currentFileOptions,
    onAddFiles,
    onDeleteFile,
  }
}
